import { useCallback } from "react";
import Axios from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login";

export interface SalesChannel {
    /** Shopify publication GID. */
    id: string;
    name: string;
    /** New products are published to this channel. */
    selected: boolean;
    isOnlineStore: boolean;
    /** Set when Shopify reports the channel isn't set up; it can't be turned on until fixed. */
    issue?: {
        kind: 'requires_action' | 'inactive';
        messages: string[];
        /** The channel's page in Shopify admin (or the sales channels settings page). */
        adminUrl?: string | null;
    } | null;
}

export interface SalesChannelsResponse {
    channels: SalesChannel[];
    /** The store hasn't granted the publications permissions — reconnect needed. */
    missingScope: boolean;
    /** False while the Online Store default applies. */
    customized: boolean;
    /** Whether Shopify answered the channel setup check; `diagnostics` is for troubleshooting only. */
    health?: { known: boolean; diagnostics: string[] };
}

/** Never throws — callers branch on `ok`. */
export type SalesChannelsResult =
    | { ok: true; data: SalesChannelsResponse }
    | { ok: false; message: string };

const errorMessage = (err: any, fallback: string): string =>
    err?.response?.data?.message || err?.message || fallback;

// #region useSalesChannels
/** Which Shopify sales channels new products are published to. */
export const useSalesChannels = () => {
    const [, authToken] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const getSalesChannels = useCallback(async (): Promise<SalesChannelsResult> => {
        try {
            const response = await Axios.get(`${environment.brandChannelUrl}/sales-channels`, { headers });
            return { ok: true, data: response.data as SalesChannelsResponse };
        } catch (err: any) {
            return { ok: false, message: errorMessage(err, 'Could not load your Shopify sales channels.') };
        }
    }, [authToken]); // eslint-disable-line

    const saveSalesChannels = useCallback(async (publicationIds: string[]): Promise<SalesChannelsResult> => {
        try {
            const response = await Axios.put(
                `${environment.brandChannelUrl}/sales-channels`,
                { publication_ids: publicationIds },
                { headers }
            );
            return { ok: true, data: response.data as SalesChannelsResponse };
        } catch (err: any) {
            return { ok: false, message: errorMessage(err, 'Could not save your sales channels.') };
        }
    }, [authToken]); // eslint-disable-line

    return { getSalesChannels, saveSalesChannels };
};
